import { Card, Button, Input, message, Space, Divider, Tag } from 'antd'
import { SaveOutlined, EyeOutlined } from '@ant-design/icons'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

const { TextArea } = Input

// المتغيرات المتاحة للإدراج - المفتاح (key) يبقى بالعربي دائماً بغض
// النظر عن لغة الواجهة، لأنه يُخزَّن حرفياً داخل القالب كـ {{المفتاح}}
// ويقرأه الباك إند (whatsappSend.js) لاستبداله بالقيمة الفعلية عند
// الإرسال — ترجمته ستكسر كل القوالب المحفوظة مسبقاً عند العملاء.
// فقط "label" (النص الظاهر على الزر) يُترجَم حسب لغة الواجهة.
const VARIABLE_KEYS = [
  { key: 'اسم_العميل', labelKey: 'messageTemplateDesigner.varCustomerName', sample: 'محمد أحمد' },
  { key: 'رقم_الفاتورة', labelKey: 'messageTemplateDesigner.varBillNo', sample: 'INV-1024' },
  { key: 'تاريخ_الفاتورة', labelKey: 'messageTemplateDesigner.varBillDate', sample: '2026/08/04' },
  { key: 'عدد_المواد', labelKey: 'messageTemplateDesigner.varItemCount', sample: '7' },
  { key: 'قيمة_الفاتورة', labelKey: 'messageTemplateDesigner.varBillAmount', sample: '1,250.00' },
]

// متغير خاص: مجرد وجوده في القالب يُفعّل إرسال PDF الفاتورة كمرفق
// (لا يُعرض كنص في الرسالة - فقط إشارة تحكم). المفتاح ثابت بالعربي
// لنفس السبب أعلاه تماماً.
const ATTACHMENT_KEY = 'مرفق_الفاتورة'

function MessageTemplateDesigner({ token, templateType = 'bill' }) {
  const { t } = useTranslation()
  const [templateText, setTemplateText] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const textAreaRef = useRef(null)
  const headers = { Authorization: `Bearer ${token}` }

  const VARIABLES = VARIABLE_KEYS.map(v => ({ ...v, label: t(v.labelKey) }))
  const ATTACHMENT_VARIABLE = { key: ATTACHMENT_KEY, label: t('messageTemplateDesigner.attachmentLabel') }

  useEffect(() => {
    fetchTemplate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchTemplate = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`/api/settings/templates/${templateType}`, { headers })
      setTemplateText(res.data.template_text)
    } catch (err) {
      console.error('template fetch error:', err.response?.data || err.message)
      message.error(err.response?.data?.error || t('messageTemplateDesigner.fetchError'))
    }
    setLoading(false)
  }

  const saveTemplate = async () => {
    setSaving(true)
    try {
      await axios.put(`/api/settings/templates/${templateType}`, { template_text: templateText }, { headers })
      message.success(t('messageTemplateDesigner.saveSuccess'))
    } catch (err) {
      console.error('template save error:', err.response?.data || err.message)
      message.error(err.response?.data?.error || t('messageTemplateDesigner.saveError'))
    }
    setSaving(false)
  }

  // إدراج متغير عند موضع الكيرسر بالضبط
  const insertVariable = (varKey) => {
    const el = textAreaRef.current?.resizableTextArea?.textArea
    const placeholder = `{{${varKey}}}`

    if (!el) {
      setTemplateText((prev) => prev + placeholder)
      return
    }

    const start = el.selectionStart
    const end = el.selectionEnd
    const newText = templateText.slice(0, start) + placeholder + templateText.slice(end)
    setTemplateText(newText)

    // إعادة تركيز الكيرسر بعد المتغير المُدرج
    setTimeout(() => {
      el.focus()
      el.selectionStart = el.selectionEnd = start + placeholder.length
    }, 0)
  }

  // معاينة الرسالة باستبدال المتغيرات بقيم تجريبية
  const previewText = () => {
    let result = templateText
    VARIABLES.forEach((v) => {
      result = result.replaceAll(`{{${v.key}}}`, v.sample)
    })
    // إزالة إشارة الإرفاق من النص (لن تظهر في الرسالة الفعلية أبداً)
    result = result
      .replaceAll(`{{${ATTACHMENT_VARIABLE.key}}}`, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
    return result
  }

  const willAttachPdf = templateText.includes(`{{${ATTACHMENT_VARIABLE.key}}}`)

  return (
    <Card title={t('messageTemplateDesigner.title')} loading={loading}>
      <p style={{ color: '#888', marginBottom: 12 }}>
        {t('messageTemplateDesigner.intro')}
      </p>

      <Space wrap style={{ marginBottom: 8 }}>
        {VARIABLES.map((v) => (
          <Tag
            key={v.key}
            color="blue"
            style={{ cursor: 'pointer', fontSize: 13, padding: '4px 10px' }}
            onClick={() => insertVariable(v.key)}
          >
            + {v.label}
          </Tag>
        ))}
      </Space>

      <div style={{ marginBottom: 16 }}>
        <Tag
          color="green"
          style={{ cursor: 'pointer', fontSize: 13, padding: '4px 10px' }}
          onClick={() => insertVariable(ATTACHMENT_VARIABLE.key)}
        >
          + {ATTACHMENT_VARIABLE.label}
        </Tag>
        <span style={{ color: '#888', fontSize: 12, marginRight: 8 }}>
          {t('messageTemplateDesigner.attachmentHint')}
        </span>
      </div>

      <TextArea
        ref={textAreaRef}
        value={templateText}
        onChange={(e) => setTemplateText(e.target.value)}
        rows={10}
        placeholder="مثال: عميلنا العزيز {{اسم_العميل}}، نُرفق فاتورة رقم {{رقم_الفاتورة}}..."
        style={{ fontSize: 15, direction: 'rtl' }}
      />

      <div style={{ marginTop: 12 }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={saveTemplate} loading={saving}>
          {t('messageTemplateDesigner.saveBtn')}
        </Button>
      </div>

      <Divider />

      <div>
        <Space style={{ marginBottom: 8 }}>
          <EyeOutlined />
          <strong>{t('messageTemplateDesigner.previewLabel')}</strong>
        </Space>
        <div
          style={{
            background: '#f6f6f6',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: 16,
            whiteSpace: 'pre-wrap',
            direction: 'rtl',
            fontSize: 15,
            minHeight: 80
          }}
        >
          {previewText() || t('messageTemplateDesigner.previewPlaceholder')}
        </div>

        <div style={{ marginTop: 8 }}>
          {willAttachPdf ? (
            <Tag color="green">{t('messageTemplateDesigner.attachYes')}</Tag>
          ) : (
            <Tag color="default">{t('messageTemplateDesigner.attachNo')}</Tag>
          )}
        </div>
      </div>
    </Card>
  )
}

export default MessageTemplateDesigner
